import { Session, SessionCacheStatus } from '@helios/state-management'

export enum HeliosMessageType {
  /**
   * Heartbeat/Healthcheck
   */
  GlobalHeartbeat = 'global/heartbeat',
  SessionHeartbeat = 'session/heartbeat',

  /**
   * Generic Session
   */
  SessionStart = 'session/start',
  SessionEnd = 'session/end',

  /**
   * Session Cache Load State
   */
  CWTransactionLoad = 'transaction/load',
  ExternalTransactionLoad = 'external-transaction/load',
  IssueLoad = 'issue/load',
  PositionLoad = 'position/load',
  DistributionLoad = 'distribution/load',
  /**
   * Flex (2.0) session-cache load state. These are the ui-events emitted for the Flex Positions /
   * Flex Transactions caches that back the Positions 2.0 and Transactions 2.0 panels. They are
   * distinct from the three legacy caches above: a CW session never emits them, and an ENFN session
   * never emits the legacy three.
   */
  FlexPositionLoad = 'flex-position/load',
  FlexExternalPositionLoad = 'flex-external-position/load',
  FlexTransactionLoad = 'flex-transaction/load',
  FlexExternalTransactionLoad = 'flex-external-transaction/load',
  AuxiliaryUpdate = 'session/auxiliary-update',
  RestrictedRequestIdsUpdate = 'session/restricted-request-ids-update',

  /**
   * CRUD
   */
  AccountCreate = 'account/create',
  AccountUpdate = 'account/update',
  AccountDelete = 'account/delete',
  CWTransactionCreate = 'transaction/create',
  CWTransactionUpdate = 'transaction/update',
  CWTransactionDelete = 'transaction/delete',
  ExternalTransactionCreate = 'external-transaction/create',
  ExternalTransactionUpdate = 'external-transaction/update',
  ExternalTransactionDelete = 'external-transaction/delete',
  IssueCreate = 'issue/create',
  IssueUpdate = 'issue/update',
  IssueDelete = 'issue/delete',
  PositionCreate = 'position/create',
  PositionUpdate = 'position/update',
  PositionDelete = 'position/delete',
  DistributionCreate = 'distribution/create',
  DistributionUpdate = 'distribution/update',
  DistributionDelete = 'distribution/delete',
  CommandsUpdate = 'commands/update',
  BreakUpdate = 'break/update',

  /**
   * Flex Transactions / External Transactions row updates.
   *
   * Emitted once flex-transactions has re-hydrated its tables after a match group changed
   * (PVAL-26291 CHANGE 4): actions endpoint -> command -> RabbitMQ -> portfolio applies to
   * `flex_matches` -> `FlexMatchEvent` -> re-hydrate -> these events. Deliberately their own types
   * rather than an overload of the load events, because a load event would push the grids back
   * through `hs-cache-load-listener`, which is not what a match needs.
   *
   * There are TWO, one per feed, because the two grids are separate: `FlexTransactionUpdate` is the
   * Enfusion (`transactions`) side and `FlexExternalTransactionUpdate` the Prometheus
   * (`external_transactions`) side — the same split as the two load events above.
   *
   * Payload is {@link RowIdPayload}, NOT a bare `string[]` — see {@link FlexRowUpdateEvent} for the
   * consumer contract, which matters: `rowIds` is not namespaced per feed.
   */
  FlexTransactionUpdate = 'flex-transaction/update',
  FlexExternalTransactionUpdate = 'flex-external-transaction/update',

  /**
   * Flex Positions per-row match-state updates — the position twin of the pair above, emitted once
   * flex-positions has re-hydrated after a match group changed. Consumed by the Positions 2.0
   * panel, which re-reads exactly those rows through `POST /flex-positions/by-ids` rather than
   * re-running its load-complete handling for a handful of rows.
   *
   * Both wire strings are CONFIRMED against `UiEventType` on helios !487 (PVAL-26414), which
   * declares `FLEX_POSITION_UPDATE("flex-position/update")` and
   * `FLEX_EXTERNAL_POSITION_UPDATE("flex-external-position/update")`. That check is not optional
   * paranoia: a wire-string mismatch fails SILENTLY — the subscription simply never fires — so
   * re-confirm these two literals if the backend MR is amended before it merges.
   *
   * Two consumer contracts, both from the backend's own note on `createPositionRowChangedEvent`,
   * and both easy to get backwards:
   *  - `rowIds` is the SAME unfiltered cross-feed union in BOTH events. It is not one event per
   *    feed carrying only that feed's ids — an id a grid does not render is a no-op, not a missing
   *    row, so a consumer must not "helpfully" filter one event down to its own source.
   *  - It is a DELTA of what changed, never a snapshot of what is currently matched.
   */
  FlexPositionUpdate = 'flex-position/update',
  FlexExternalPositionUpdate = 'flex-external-position/update',

  /**
   * Generic Error
   */
  GlobalError = 'global/error',
  SessionError = 'session/error',

  /**
   * Jobs
   */
  JobComplete = 'job/complete',
  JobFailed = 'job/failed',

  /**
   * Filters
   */
  AddFilters = 'filters/add',
  ClearFilters = 'filters/clear',

  /**
   * Authenticate
   */
  Authenticate = 'auth',
}

export type HeliosMessage<T> = {
  id: string
  timestampUtc: string
  eventType: HeliosMessageType
  heliosSessionId?: string
  payload: T
}

export interface GlobalEvent<T> extends HeliosMessage<T> {
  heliosSessionId: undefined
}

export interface SessionBasedEvent<T> extends HeliosMessage<T> {
  heliosSessionId: string
}

export interface SessionStartEvent extends SessionBasedEvent<Session> {
  eventType: HeliosMessageType.SessionStart
}

export interface SessionEndEvent extends SessionBasedEvent<never> {
  eventType: HeliosMessageType.SessionEnd
}

export interface LoadEvent
  extends SessionBasedEvent<{
    status: SessionCacheStatus
    errorDetails?: StandardApiError
  }> {
  eventType:
    | HeliosMessageType.CWTransactionLoad
    | HeliosMessageType.ExternalTransactionLoad
    | HeliosMessageType.PositionLoad
    | HeliosMessageType.FlexPositionLoad
    | HeliosMessageType.FlexExternalPositionLoad
    | HeliosMessageType.FlexTransactionLoad
    | HeliosMessageType.FlexExternalTransactionLoad
}

export interface RowCreateEvent extends SessionBasedEvent<string[]> {
  eventType:
    | HeliosMessageType.CWTransactionCreate
    | HeliosMessageType.ExternalTransactionCreate
    | HeliosMessageType.IssueCreate
    | HeliosMessageType.PositionCreate
    | HeliosMessageType.DistributionCreate
}

export interface RowUpdateEvent extends SessionBasedEvent<string[]> {
  eventType:
    | HeliosMessageType.CWTransactionUpdate
    | HeliosMessageType.ExternalTransactionUpdate
    | HeliosMessageType.IssueUpdate
    | HeliosMessageType.PositionUpdate
    | HeliosMessageType.DistributionUpdate
}

/**
 * Payload of a Flex position row-update event.
 *
 * Two shapes are accepted deliberately. `{ rowIds }` is the shape the backend has been asked for,
 * but the bare `string[]` is what every other per-row event in this file already carries (see
 * {@link RowUpdateEvent}), and Flex External Transactions hedges the same way
 * (`ExtTranSSEEventPayload`). Since the producing side does not exist yet, tolerating both is what
 * stops a shape mismatch from shipping a subscription that silently never does anything.
 */
export type FlexPositionRowUpdatePayload = { rowIds: string[] } | string[]

/**
 * Deliberately its own interface rather than two more members on {@link RowUpdateEvent}: that union
 * is narrowed on `string[]` payloads app-wide, and widening its payload type would change existing
 * narrowing for every consumer.
 */
export interface FlexPositionRowUpdateEvent
  extends SessionBasedEvent<FlexPositionRowUpdatePayload> {
  eventType:
    | HeliosMessageType.FlexPositionUpdate
    | HeliosMessageType.FlexExternalPositionUpdate
}

/**
 * Row ids named by a Flex position row-update event, normalised across both accepted payload
 * shapes. Anything else — `null`, `{}`, a non-array `rowIds` — yields no ids rather than throwing:
 * an unrecognised payload must be a no-op, not a broken panel.
 */
export function flexPositionRowIds(
  payload: FlexPositionRowUpdatePayload | null | undefined,
): string[] {
  if (Array.isArray(payload))
    return payload.filter(id => typeof id === 'string')
  const rowIds = payload?.rowIds
  if (!Array.isArray(rowIds)) return []
  return rowIds.filter(id => typeof id === 'string')
}

export interface RowDeleteEvent extends SessionBasedEvent<string[]> {
  eventType:
    | HeliosMessageType.CWTransactionDelete
    | HeliosMessageType.ExternalTransactionDelete
    | HeliosMessageType.IssueDelete
    | HeliosMessageType.PositionDelete
    | HeliosMessageType.DistributionDelete
}

export interface AccountRowCreateEvent extends GlobalEvent<number[]> {
  eventType: HeliosMessageType.AccountCreate
}

export interface AccountRowUpdateEvent<T>
  extends GlobalEvent<{
    accountId: number
    //In order to avoid a circular dependency I used T instead of Account.
    updatedFields: (keyof T)[]
  }> {
  eventType: HeliosMessageType.AccountUpdate
}

export interface AccountRowDeleteEvent extends GlobalEvent<number[]> {
  eventType: HeliosMessageType.AccountDelete
}

export interface SessionErrorEvent extends SessionBasedEvent<StandardApiError> {
  eventType: HeliosMessageType.SessionError
}

export interface GlobalErrorEvent extends SessionBasedEvent<StandardApiError> {
  eventType: HeliosMessageType.GlobalError
}

export interface JobCompleteEvent extends GlobalEvent<JobCompletePayload> {
  eventType: HeliosMessageType.JobComplete
}

export interface JobFailedEvent extends GlobalEvent<JobPayload> {
  eventType: HeliosMessageType.JobFailed
}

export interface JobCompletePayload {
  jobId: number
  affectedAccountIds: number[]
}

export interface JobPayload {
  jobId: number
  userId: number
  accountId: number
  reconDate: string
}

export interface StandardApiError {
  errorId?: string
  displayMessage?: string
  errorMessage?: string
  stackTrace?: string
}

export interface CommandsUpdateEvent
  extends GlobalEvent<CommandsUpdatePayload[]> {
  eventType: HeliosMessageType.CommandsUpdate
}

export interface CommandsUpdatePayload {
  accountId: number
  commandCount: number
}

export interface BreakUpdateEvent extends SessionBasedEvent<string[]> {
  eventType: HeliosMessageType.BreakUpdate
}

/** The affected row ids of a flex row-update event. Mirrors the server's `RowIdPayload`. */
export interface RowIdPayload {
  rowIds: string[]
}

/**
 * A flex match group changed — created, added to, or dissolved.
 *
 * IMPORTANT consumer contract (confirmed against beta): `rowIds` is NOT namespaced per feed. The
 * server passes the whole of `FlexMatchEvent.entityIds` unfiltered into both events, and a match
 * group spans both feeds — so each payload is the same cross-feed union of Enfusion transaction ids
 * and Prometheus ext-tran ids. A consumer must intersect `rowIds` against the rows it actually
 * renders and treat an unknown id as a no-op, never as a row it is missing.
 *
 * Note also that a brand-new `FLEX_MATCH` does NOT produce one of these in-session: portfolio has no
 * handler for a create (perseus mints the group id), so a first-time match only becomes visible
 * after the next Run. `FLEX_ADD_MATCH` and `FLEX_UNMATCH` both name an existing group and do apply
 * immediately.
 */
export interface FlexRowUpdateEvent extends SessionBasedEvent<RowIdPayload> {
  eventType:
    | HeliosMessageType.FlexTransactionUpdate
    | HeliosMessageType.FlexExternalTransactionUpdate
}

export interface AuxiliaryUpdateEvent
  extends SessionBasedEvent<AuxiliaryUpdatePayload> {
  eventType: HeliosMessageType.AuxiliaryUpdate
}

export interface AuxiliaryUpdatePayload {
  // Using Record<string, unknown> instead of any for better type safety
  [key: string]: unknown
}
